import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Layout } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { getTextbooks, getChapters } from "@/lib/api/textbooks"
import { getUserProgress, setUserProgress } from "@/lib/api/user"
import {
  ProgressList,
  ProgressPicker,
} from "@/components/textbook-progress-picker"
import { Skeleton } from "@/components/ui/skeleton"
import { BackLink } from "@/components/back-link"
import { useIsDesktop } from "@/hooks/use-is-desktop"
import {
  useProgressSelection,
  type ProgressSelection,
} from "@/hooks/use-progress-selection"

function Settings() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()

  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ["user-progress"],
    queryFn: getUserProgress,
  })

  const { data: textbooks = [] } = useQuery({
    queryKey: ["textbooks"],
    queryFn: getTextbooks,
  })

  const selection = useProgressSelection(progress)
  const { textbookId, chapterNumber, pendingSave } = selection

  const { data: chapters = [] } = useQuery({
    queryKey: ["chapters", textbookId],
    queryFn: () => getChapters(textbookId!),
    enabled: !!textbookId,
  })

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: (data: ProgressSelection) => setUserProgress(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user-progress"] })
      await queryClient.invalidateQueries({ queryKey: ["knowledge"] })
      navigate({ to: "/stories" })
    },
  })
  const save = () => pendingSave && mutate(pendingSave)

  const pickerProps = {
    textbooks,
    selectedTextbookId: textbookId,
    onTextbookChange: selection.selectTextbook,
    chapters,
    selectedChapterNumber: chapterNumber,
    onChapterSelect: selection.selectChapter,
  }

  if (progressLoading || textbooks.length === 0)
    return (
      <Layout title="Settings">
        <BackLink to="/stories" className="mb-8 hidden desktop:inline-flex" />
        <div className="pb-12">
          <div className="mb-8">
            <Skeleton className="mb-2 h-6 w-24" />
            <Skeleton className="h-10 w-full max-w-sm" />
          </div>
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    )

  if (!isDesktop)
    return (
      <Layout title="Settings">
        <ProgressList {...pickerProps} />
        {/* Sits above the tab bar; Track A's shell sets --tab-bar-height. */}
        <div className="sticky bottom-[calc(var(--tab-bar-height,calc(3.0625rem+env(safe-area-inset-bottom)))+0.75rem)] mt-6">
          <Button
            size="lg"
            className="h-12 w-full text-[17px]"
            onClick={save}
            disabled={isSaving || !pendingSave}
          >
            Save
          </Button>
        </div>
      </Layout>
    )

  return (
    <Layout title="Settings">
      <BackLink to="/stories" className="mb-8" />
      <ProgressPicker
        {...pickerProps}
        footer={
          !selection.isChanged && chapterNumber !== null ? (
            <div className="flex justify-end">
              <Button variant="outline" disabled>
                Applied
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button onClick={save} disabled={isSaving || !pendingSave}>
                Apply
              </Button>
            </div>
          )
        }
      />
    </Layout>
  )
}

export const Route = createFileRoute("/_authenticated/settings")({
  component: Settings,
})
